import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AiChatService } from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DynamicActionCardPayload } from '../../../models/action-card.types';

/**
 * SD-2326: renders a dynamic action-card pushed by the BE's Langgraph
 * proposeAction interrupt. Editable form is generated from the card's
 * {@code editableFields}; Accept POSTs the (possibly edited) values to
 * `/api/v1/allo/approve-action` and resumes the graph, Reject sends the
 * same endpoint with {@code approved=false}.
 */
@Component({
  selector: 'qt-ai-chat-approval-card',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslationCoreModule,
  ],
  templateUrl: './ai-chat-approval-card.component.html',
  styleUrls: ['./ai-chat-approval-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatApprovalCardComponent {
  private readonly chatSvc = inject(AiChatService);
  private readonly destroyRef = inject(DestroyRef);

  readonly payload = input.required<DynamicActionCardPayload>();
  readonly pendingApprovalId = input.required<string>();
  readonly chatId = input.required<string>();

  readonly resolved = output<void>();

  protected readonly formValues = signal<Record<string, unknown>>({});
  protected readonly submitting = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly decision = signal<'approved' | 'rejected' | null>(null);

  constructor() {
    // Seed the form once the payload arrives. Prefer the BE's proposed values;
    // fall back to empty so the input bindings always have a defined value.
    queueMicrotask(() => {
      const card = this.payload().card;
      const seed: Record<string, unknown> = {};
      for (const field of card.editableFields) {
        seed[field.key] = card.values?.[field.key] ?? '';
      }
      this.formValues.set(seed);
    });
  }

  protected updateField(key: string, value: unknown): void {
    this.formValues.update(prev => ({ ...prev, [key]: value }));
  }

  protected accept(): void {
    this.submit(true);
  }

  protected reject(): void {
    this.submit(false);
  }

  private submit(approved: boolean): void {
    if (this.submitting() || this.decision() !== null) return;

    this.submitting.set(true);
    this.error.set(null);

    const edited = approved ? this.diffEditedFields() : undefined;

    this.chatSvc
      .submitApproval({
        chatId: this.chatId(),
        pendingApprovalId: this.pendingApprovalId(),
        decision: approved ? 'approve' : 'reject',
        editedFields: edited,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.decision.set(approved ? 'approved' : 'rejected');
          // Give the user a moment to see the confirmation before dismissing.
          setTimeout(() => this.resolved.emit(), 1500);
        },
        error: () => {
          this.submitting.set(false);
          this.error.set('GNR_ALLO_APPROVAL_FAILED');
        },
      });
  }

  /**
   * Send only fields the user actually changed — keeps the BE-side proposed
   * values authoritative for anything untouched. Empty diff returns undefined
   * so the BE applies its proposed values unchanged.
   */
  private diffEditedFields(): Record<string, unknown> | undefined {
    const proposed = this.payload().card.values ?? {};
    const current = this.formValues();
    const diff: Record<string, unknown> = {};
    let anyChange = false;
    for (const key of Object.keys(current)) {
      if (current[key] !== proposed[key]) {
        diff[key] = current[key];
        anyChange = true;
      }
    }
    return anyChange ? diff : undefined;
  }
}
